import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useMedicalHistoryMutations } from '@/features/patient-record/hooks/useMedicalHistoryMutations';
import type {
  MeasurementType,
  MeasurementViewModel,
} from '@/features/patient-record/types';

import { computeAgeInMonths } from './ageInMonths';
import { MeasurementFieldRow } from './MeasurementFieldRow';
import {
  EDITABLE_TYPES,
  hasValidValue,
  toDraft,
  toQualifiers,
  type FieldDraft,
  type MeasurementDraft,
} from './measurementDraft';
import type { UnitSystem } from './unitConversion';

interface MeasurementFormProps {
  readonly guid: string;
  readonly dateOfBirth: string | null;
  /** When provided, the dialog switches to **edit mode** — fields are
   *  prefilled from the record and Save PUTs the existing row. When
   *  omitted, the dialog is in **create mode**: a new record is POSTed. */
  readonly record?: MeasurementViewModel;
  /** Card-level metric/imperial toggle. Drives the initial unit of each
   *  field row so edit values render in the same system the table shows. */
  readonly unitSystem: UnitSystem;
  readonly onClose: () => void;
}

/**
 * Modal used for both **create** and **edit** (mirrors the allergy /
 * genetic-variant dialogs). One date + Weight / Height / Head Circumference /
 * Arm Span rows + Notes. Each non-empty field becomes a qualifier on the
 * resulting record; clearing a field removes that qualifier (edit only).
 * Storage stays metric — the per-row unit picker only affects the conversion
 * before send. The draft commits only on the explicit Add / Save button; the
 * dirty guard raises Save / Discard / Keep editing on a dirty dismiss.
 */
export function MeasurementForm({
  guid,
  dateOfBirth,
  record,
  unitSystem,
  onClose,
}: MeasurementFormProps): ReactElement {
  const { t } = useTranslation();
  const { addMeasurement, updateMeasurement } = useMedicalHistoryMutations(guid);
  const isEdit = record !== undefined;
  const label = t('patient.medicalHistory.measurementFormLabel', { defaultValue: 'Measurement' });

  const pending = addMeasurement.isPending || updateMeasurement.isPending;

  const initial = useMemo(() => toDraft(record, unitSystem), [record, unitSystem]);

  const toInput = (d: MeasurementDraft) => {
    const trimmedNotes = d.notes.trim();
    return {
      dateMeasured: d.date,
      ageInMonths: computeAgeInMonths(dateOfBirth, d.date),
      notes: trimmedNotes.length > 0 ? trimmedNotes : null,
      qualifiers: toQualifiers(d),
    };
  };

  const commit = (d: MeasurementDraft): Promise<unknown> =>
    record !== undefined
      ? updateMeasurement.mutateAsync({ id: record.id, input: toInput(d) })
      : addMeasurement.mutateAsync(toInput(d));

  const form = useEditableDraft<MeasurementDraft>({
    initial,
    label,
    autosave: false,
    canSave: (d) => d.date.length > 0 && hasValidValue(d),
    onSave: (d) => commit(d),
  });
  const { draft } = form;

  const setField = (type: MeasurementType) => (next: FieldDraft): void => {
    form.setDraft((d) => ({ ...d, [type]: next }));
  };

  // The explicit Add / Save button: commit AND close.
  const submitAndClose = (): void => {
    if (!form.canSave) return;
    void commit(draft).then(onClose).catch(() => undefined);
  };

  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: pending,
    label,
    onSave: submitAndClose,
    onClose,
    register: false,
  });

  return (
    <>
      <Dialog open {...guard.rootProps}>
        <DialogContent
          className="sm:max-w-lg max-h-[calc(100vh-3rem)] p-0 gap-0 flex flex-col"
          {...guard.contentProps}
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle>
              {isEdit
                ? t('patient.medicalHistory.editMeasurement')
                : t('patient.medicalHistory.addMeasurement')}
            </DialogTitle>
          </DialogHeader>
          <form
            ref={form.containerProps.ref}
            className="flex flex-col min-h-0 flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              submitAndClose();
            }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
              <label className="flex flex-col gap-1.5 text-xs font-medium sm:w-1/2 sm:pr-2">
                {t('patient.medicalHistory.measurement.dateColumn')}
                <DateInput
                  value={draft.date}
                  onChange={(value) => {
                    form.setDraft((d) => ({ ...d, date: value }));
                  }}
                />
              </label>

              {EDITABLE_TYPES.map((type) => (
                <MeasurementFieldRow
                  key={type}
                  type={type}
                  label={t(`patient.medicalHistory.measurement.type.${type}`)}
                  field={draft[type]}
                  onChange={setField(type)}
                />
              ))}

              <label className="flex flex-col gap-1.5 text-xs font-medium">
                {t('patient.medicalHistory.measurement.notesLabel')}
                <textarea
                  value={draft.notes}
                  onChange={(event) => {
                    form.setDraft((d) => ({ ...d, notes: event.target.value }));
                  }}
                  rows={2}
                  className="w-full rounded border border-input bg-card px-2 py-1.5 text-sm"
                />
              </label>
            </div>

            <DialogFooter className="shrink-0 border-t border-border px-6 py-3">
              <Button
                type="button"
                variant="ghost"
                onClick={guard.requestClose}
                disabled={pending}
              >
                {t('ui.dialog.close')}
              </Button>
              <Button
                type="submit"
                disabled={!form.canSave || pending}
                loading={pending}
                loadingText={t('patient.dirtyFooter.saving')}
              >
                {isEdit
                  ? t('patient.medicalHistory.save')
                  : t('patient.medicalHistory.addMeasurement')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {guard.confirm}
    </>
  );
}
